function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Copyright IBM Corp. 2016, 2018
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import classNames from 'classnames';
import { settings } from 'carbon-components';
import WarningFilled16 from '@carbon/icons-react/lib/warning--filled/16';
import PasswordInput from './PasswordInput';
import ControlledPasswordInput from './ControlledPasswordInput';
import { textInputProps } from './util';
var prefix = settings.prefix;

var DefaultCharCounter = function DefaultCharCounter(_ref) {
  var disabled = _ref.disabled,
      count = _ref.count,
      maxLength = _ref.maxLength;
  var charCounterClasses = classNames("".concat(prefix, "--text-input--character-counter"), _defineProperty({}, "".concat(prefix, "--text-input--character-counter--disabled"), disabled));
  return React.createElement("span", {
    className: charCounterClasses
  }, React.createElement("span", {
    className: "".concat(prefix, "--text-input--character-counter--length")
  }, count), "/", React.createElement("span", {
    className: "".concat(prefix, "--text-input--character-counter--maxlength")
  }, maxLength));
};

var TextInput = React.forwardRef(function TextInput(_ref2, ref) {
  var _classNames2, _classNames3;

  var labelText = _ref2.labelText,
      _ref2$className = _ref2.className,
      className = _ref2$className === void 0 ? "".concat(prefix, "--text__input") : _ref2$className,
      id = _ref2.id,
      placeholder = _ref2.placeholder,
      type = _ref2.type,
      _onChange = _ref2.onChange,
      _onClick = _ref2.onClick,
      hideLabel = _ref2.hideLabel,
      invalid = _ref2.invalid,
      invalidText = _ref2.invalidText,
      helperText = _ref2.helperText,
      light = _ref2.light,
      charCount = _ref2.charCount,
      _ref2$renderCharCount = _ref2.renderCharCounter,
      CharCounter = _ref2$renderCharCount === void 0 ? DefaultCharCounter : _ref2$renderCharCount,
      defaultValue = _ref2.defaultValue,
      maxLength = _ref2.maxLength,
      other = _objectWithoutProperties(_ref2, ["labelText", "className", "id", "placeholder", "type", "onChange", "onClick", "hideLabel", "invalid", "invalidText", "helperText", "light", "charCount", "renderCharCounter", "defaultValue", "maxLength"]);

  var _useState = useState(defaultValue),
      _useState2 = _slicedToArray(_useState, 2),
      inputVal = _useState2[0],
      setInput = _useState2[1];

  var errorId = id + '-error-msg';
  var textInputClasses = classNames("".concat(prefix, "--text-input"), className, (_classNames2 = {}, _defineProperty(_classNames2, "".concat(prefix, "--text-input--light"), light), _defineProperty(_classNames2, "".concat(prefix, "--text-input--invalid"), invalid), _classNames2));

  var sharedTextInputProps = _objectSpread({
    id: id,
    onChange: function onChange(evt) {
      if (!other.disabled) {
        _onChange(evt);
      }
    },
    onClick: function onClick(evt) {
      if (!other.disabled) {
        _onClick(evt);
      }
    },
    placeholder: placeholder,
    type: type,
    ref: ref,
    className: textInputClasses,
    maxLength: maxLength || null
  }, other);

  var labelClasses = classNames("".concat(prefix, "--label"), (_classNames3 = {}, _defineProperty(_classNames3, "".concat(prefix, "--visually-hidden"), hideLabel), _defineProperty(_classNames3, "".concat(prefix, "--label--disabled"), other.disabled), _classNames3));
  var helperTextClasses = classNames("".concat(prefix, "--form__helper-text"), _defineProperty({}, "".concat(prefix, "--form__helper-text--disabled"), other.disabled));

  var label = function () {
    var labelContent = labelText && React.createElement("label", {
      htmlFor: id,
      className: labelClasses
    }, labelText);

    if (labelContent && charCount) {
      return React.createElement("div", {
        className: "".concat(prefix, "--text-input__character-counter-title")
      }, labelContent, React.createElement(CharCounter, {
        disabled: other.disabled,
        count: inputVal.length,
        maxLength: maxLength
      }));
    }

    return labelContent;
  }();

  var error = invalid ? React.createElement("div", {
    className: "".concat(prefix, "--form-requirement"),
    id: errorId
  }, invalidText) : null;
  var input = React.createElement("input", _extends({}, textInputProps({
    invalid: invalid,
    sharedTextInputProps: sharedTextInputProps,
    errorId: errorId
  }), {
    value: inputVal,
    onInput: function onInput(e) {
      return setInput(e.target.value);
    }
  }));

  var helper = function () {
    var helperContent = helperText ? React.createElement("div", {
      className: helperTextClasses
    }, helperText) : null;

    if (!labelText && charCount) {
      return React.createElement("div", {
        className: "".concat(prefix, "--text-input__character-counter-title")
      }, helperContent, React.createElement(CharCounter, {
        disabled: other.disabled,
        count: inputVal.length,
        maxLength: maxLength
      }));
    }

    return helperContent;
  }();

  return React.createElement("div", {
    className: "".concat(prefix, "--form-item ").concat(prefix, "--text-input-wrapper")
  }, label, helper, React.createElement("div", {
    className: "".concat(prefix, "--text-input__field-wrapper"),
    "data-invalid": invalid || null
  }, invalid && React.createElement(WarningFilled16, {
    className: "".concat(prefix, "--text-input__invalid-icon")
  }), input), error);
});
TextInput.PasswordInput = PasswordInput;
TextInput.ControlledPasswordInput = ControlledPasswordInput;
TextInput.propTypes = {
  /**
   * Specify an optional className to be applied to the <input> node
   */
  className: PropTypes.string,

  /**
   * Optionally provide the default value of the <input>
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Specify whether the <input> should be disabled
   */
  disabled: PropTypes.bool,

  /**
   * Specify a custom `id` for the <input>
   */
  id: PropTypes.string.isRequired,

  /**
   * Provide the text that will be read by a screen reader when visiting this
   * control
   */
  labelText: PropTypes.node.isRequired,

  /**
   * Optionally provide an `onChange` handler that is called whenever <input>
   * is updated
   */
  onChange: PropTypes.func,

  /**
   * Optionally provide an `onClick` handler that is called whenever the
   * <input> is clicked
   */
  onClick: PropTypes.func,

  /**
   * Specify the placeholder attribute for the <input>
   */
  placeholder: PropTypes.string,

  /**
   * Specify the type of the <input>
   */
  type: PropTypes.string,

  /**
   * Specify the value of the <input>
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Specify whether you want the underlying label to be visually hidden
   */
  hideLabel: PropTypes.bool,

  /**
   * Specify whether the control is currently invalid
   */
  invalid: PropTypes.bool,

  /**
   * Provide the text that is displayed when the control is in an invalid state
   */
  invalidText: PropTypes.string,

  /**
   * Provide text that is used alongside the control label for additional help
   */
  helperText: PropTypes.node,

  /**
   * `true` to use the light version.
   */
  light: PropTypes.bool,

  /**
   * Specify whether the character counter is shown
   */
  charCount: PropTypes.bool,

  /**
   * The maximum allowed input value length
   */
  maxLength: PropTypes.number
};
TextInput.defaultProps = {
  disabled: false,
  type: 'text',
  onChange: function onChange() {},
  onClick: function onClick() {},
  invalid: false,
  invalidText: '',
  helperText: '',
  light: false
};
export default TextInput;